import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Upload, ShoppingCart, Timer, DollarSign, LineChart, MessageSquareText, ClipboardList, ShieldCheck, LogOut, Menu, X, CreditCard, FileCode2, BellRing, MapPin, BarChart3, Palette } from "lucide-react";
import { LogoLockup } from "./Logo";
import { useAuth } from "../lib/AuthContext";
import CommandPalette from "./CommandPalette";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "sidebar-dashboard" },
  { to: "/app/upload", label: "Uploads", icon: Upload, testid: "sidebar-uploads" },
  { to: "/app/reorder", label: "Reorder", icon: ShoppingCart, testid: "sidebar-reorder" },
  { to: "/app/slow", label: "Slow Inventory", icon: Timer, testid: "sidebar-slow" },
  { to: "/app/pricing", label: "Pricing", icon: DollarSign, testid: "sidebar-pricing" },
  { to: "/app/market", label: "Market", icon: LineChart, testid: "sidebar-market" },
  { to: "/app/recommendations", label: "Recommendations", icon: ClipboardList, testid: "sidebar-recommendations" },
  { to: "/app/report-card", label: "Report Card", icon: BarChart3, testid: "sidebar-report-card" },
  { to: "/app/ask", label: "Ask TreadMargin", icon: MessageSquareText, testid: "sidebar-ask" },
];

const SETTINGS_NAV = [
  { to: "/app/settings/billing", label: "Billing", icon: CreditCard, testid: "sidebar-settings-billing" },
  { to: "/app/settings/locations", label: "Locations", icon: MapPin, testid: "sidebar-settings-locations" },
  { to: "/app/settings/templates", label: "Templates", icon: FileCode2, testid: "sidebar-settings-templates" },
  { to: "/app/settings/preferences", label: "Digest", icon: BellRing, testid: "sidebar-settings-preferences" },
  { to: "/app/settings/brand", label: "Brand kit", icon: Palette, testid: "sidebar-settings-brand" },
];

const suffixMobile = (id) => `${id}-mobile`;

export default function DashboardLayout({ children, demoBanner = false, onToggleDemo, demoMode }) {
  const { user, org, signout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--tm-off)] flex flex-col lg:flex-row">
      <CommandPalette />
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-[var(--tm-border)]">
        <div className="flex items-center justify-between px-5 h-14">
          <Link to="/app"><LogoLockup size={26} /></Link>
          <button className="p-2 -mr-2" onClick={() => setOpen(!open)} data-testid="dashboard-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
        {open && (
          <div className="border-t border-[var(--tm-border)] bg-white">
            <div className="p-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={suffixMobile(n.testid)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B]"}`}>
                  <n.icon size={17} /> {n.label}
                </NavLink>
              ))}
              <div className="mt-2 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#3B424B]">Settings</div>
              {SETTINGS_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={suffixMobile(n.testid)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B]"}`}>
                  <n.icon size={17} /> {n.label}
                </NavLink>
              ))}
              {user?.is_admin && (
                <NavLink to="/app/admin" onClick={() => setOpen(false)} data-testid="sidebar-admin-mobile"
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B]"}`}>
                  <ShieldCheck size={17} /> Admin
                </NavLink>
              )}
              <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#3B424B] mt-2" onClick={() => { signout(); nav("/"); }} data-testid="dashboard-signout-mobile"><LogOut size={17} /> Sign Out</button>
            </div>
          </div>
        )}
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-[var(--tm-border)] min-h-screen sticky top-0" data-testid="dashboard-sidebar">
        <div className="p-5 border-b border-[var(--tm-border)]">
          <Link to="/app"><LogoLockup /></Link>
          {org && <div className="mt-3 text-xs uppercase tracking-wider text-[#3B424B] font-semibold">{org.name}</div>}
          <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))} className="mt-4 w-full h-9 px-3 rounded-lg border border-[var(--tm-border-strong)] bg-[var(--tm-off)] text-[#3B424B] text-xs flex items-center justify-between hover:text-[#0F1115] transition-colors" data-testid="sidebar-search-trigger">
            <span>Search…</span>
            <span className="font-mono">⌘K</span>
          </button>
        </div>
        <nav className="p-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B] hover:bg-[var(--tm-off)] hover:text-[#0F1115]"}`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
          <div className="mt-3 mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#3B424B]">Settings</div>
          {SETTINGS_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B] hover:bg-[var(--tm-off)] hover:text-[#0F1115]"}`}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink to="/app/admin" data-testid="sidebar-admin"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[var(--tm-off)] text-[#0F1115] font-semibold" : "text-[#3B424B] hover:bg-[var(--tm-off)] hover:text-[#0F1115]"}`}>
              <ShieldCheck size={17} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="mt-auto p-3 border-t border-[var(--tm-border)]">
          <div className="px-3 py-2 text-xs text-[#3B424B]">
            <div className="font-semibold text-[#0F1115]">{user?.full_name || user?.email}</div>
            <div className="truncate">{user?.email}</div>
          </div>
          <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#3B424B] hover:bg-[var(--tm-off)] transition-colors" onClick={() => { signout(); nav("/"); }} data-testid="dashboard-signout"><LogOut size={17} /> Sign Out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {demoMode ? (
          <div className="bg-[var(--tm-charcoal)] text-white text-sm">
            <div className="tm-container flex items-center justify-between py-2.5 gap-3">
              <span className="text-[13px]"><span className="font-semibold text-[#C9943D]">Demo Mode</span> — Illustrative Demo Data. Not real dealer or competitor data.</span>
              {onToggleDemo && (
                <button onClick={onToggleDemo} className="text-[12px] underline underline-offset-2 hover:text-[#C9943D]" data-testid="toggle-demo">Exit demo</button>
              )}
            </div>
          </div>
        ) : onToggleDemo ? (
          <div className="bg-white border-b border-[var(--tm-border)]">
            <div className="tm-container flex items-center justify-end py-2 gap-3">
              <button onClick={onToggleDemo} className="text-[12px] text-[#3B424B] hover:text-[#0F1115] underline underline-offset-2" data-testid="toggle-demo">Enable demo mode</button>
            </div>
          </div>
        ) : null}
        <div className="p-5 sm:p-8">{children}</div>
      </div>
    </div>
  );
}
